import React, {FC, Suspense} from 'react';
import {Platform} from 'react-native';
import {RouteObject, useRoutes} from 'react-router-native';
import {View} from '@lookiero/aurora';
import {useApp} from '@app';
import {useWebview, Webview} from '../../../ui/app/components/Webview';
import {ROUTE_CONFIG} from '../routes.config';
import type {RouteHandle} from '../routes.config';
import {AuthRedirect} from './AutomaticRedirects';
import {RequireAuth} from './RequireAuth';
import {RequireGuest} from './RequireGuest';
import {MaintenanceChecker} from '@modules/maintenance';

const IS_WEB = Platform.OS === 'web';

type ConfiguredRoute = RouteObject & {handle: RouteHandle};

const {publicRoutes, privateRoutes, anyAuthRoutes} = ROUTE_CONFIG.reduce<{
  publicRoutes: ConfiguredRoute[];
  privateRoutes: ConfiguredRoute[];
  anyAuthRoutes: ConfiguredRoute[];
}>(
  (acc, route) => {
    if (route.handle.authorization === 'any') {
      return {...acc, anyAuthRoutes: [...acc.anyAuthRoutes, route]};
    }

    return route.handle.authorization
      ? {...acc, privateRoutes: [...acc.privateRoutes, route]}
      : {...acc, publicRoutes: [...acc.publicRoutes, route]};
  },
  {publicRoutes: [], privateRoutes: [], anyAuthRoutes: []},
);

const Routing: FC = () => {
  const {authorization} = useApp();
  const {link} = useWebview();

  const element = useRoutes([
    ...anyAuthRoutes,
    {element: <RequireGuest />, children: publicRoutes},
    {element: <RequireAuth />, children: privateRoutes},
    {path: '*', element: <AuthRedirect />},
  ]);

  if (!IS_WEB && link) return <Webview link={link} />;

  if (authorization === undefined) return <View />;

  return (
    <Suspense fallback={<View />}>
      <MaintenanceChecker>{element}</MaintenanceChecker>
    </Suspense>
  );
};

export {Routing};
